import React, { useState, FormEvent, ChangeEvent } from "react";
import "./ForgotPassword.less";
import { toast } from "react-toastify";
import FlyoutLayout from "../../shared/FlyoutLayout/FlyoutLayout";
import service from "../../services/service";

interface ForgotPasswordProps {
  isOpen: boolean;
  closeModal: () => void;
  onBackToLogin: () => void;
  onProceedToOTP?: (email: string) => void;
}

const ForgotPassword: React.FC<ForgotPasswordProps> = ({
  isOpen,
  closeModal,
  onBackToLogin,
  onProceedToOTP,
}) => {
  const displayName = "cmp-forgotpassword";
  const mailLogo = new URL("../../assets/mail.svg", import.meta.url).href;

  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [emailSent, setEmailSent] = useState(false);
  const [disableBtn, setDisableBtn] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  React.useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  if (!isOpen) return null;

  const sendResetEmail = async (isResend = false) => {
    setError(null);

    if (!email.trim()) {
      setError("Email cannot be empty");
      return;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError("Please enter a valid email address");
      return;
    }

    setDisableBtn(true);

    try {
      const response = await service.requestPasswordReset(email);

      if (response.message === "User not found") {
        toast.success(
          "If an account with this email exists, a password reset link has been sent."
        );
        if (onProceedToOTP) {
          onProceedToOTP(email);
        } else {
          setEmailSent(true);
        }
      } else {
        toast.success("Password reset link has been sent to your email!");
        if (onProceedToOTP) {
          onProceedToOTP(email);
        } else {
          setEmailSent(true);
        }
      }
      if (isResend) {
        setResendCooldown(60);
      }
    } catch (error: any) {
      console.error("Failed to send password reset email:", error);

      let errorMessage = "Failed to send reset email. Please try again.";

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setDisableBtn(false);
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    await sendResetEmail(false);
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    await sendResetEmail(true);
  };

  const handleBackToLogin = () => {
    setEmail("");
    setError(null);
    setEmailSent(false);
    setResendCooldown(0);
    onBackToLogin();
  };

  return (
    <FlyoutLayout
      displayName={displayName}
      isOpen={isOpen}
      closeModal={closeModal}
      contentPadding="100px 32px"
      contentAlign="center"
      contentJustify="center"
    >
      {emailSent ? (
        <>
          <div className={`${displayName}__success-container`}>
            <div className={`${displayName}__success-icon`}>
              <svg
                width="60"
                height="60"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="#10B981"
                  strokeWidth="2"
                  fill="none"
                />
                <path
                  d="m9 12 2 2 4-4"
                  stroke="#10B981"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>

            <div className={`${displayName}__header`}>
              <h2
                className={`${displayName}__title ${displayName}__title--success`}
              >
                Reset Link Sent!
              </h2>
              <p className={`${displayName}__subtitle`}>
                We've sent a password reset link to <strong>{email}</strong>
              </p>
              <p className={`${displayName}__info-text`}>
                Please check your email and click the reset link to create a new
                password. The link will expire in 10 min for security reasons.
              </p>
            </div>

            <div className={`${displayName}__resend-section`}>
              <p className={`${displayName}__resend-text`}>
                Didn't receive the email?{" "}
                <button
                  type="button"
                  className={`${displayName}__link ${
                    resendCooldown > 0 || disableBtn
                      ? `${displayName}__link--disabled`
                      : ""
                  }`}
                  onClick={handleResend}
                  disabled={resendCooldown > 0 || disableBtn}
                >
                  {resendCooldown > 0
                    ? `Resend in ${resendCooldown}s`
                    : disableBtn
                    ? "Sending..."
                    : "Resend Reset Link"}
                </button>
              </p>
            </div>

            <div className={`${displayName}__submit-btn`}>
              <button
                className={`${displayName}__submit`}
                onClick={handleBackToLogin}
                type="button"
              >
                Back to Login
              </button>
            </div>
          </div>
        </>
      ) : (
        <>
          {/* Form State */}
          <div className={`${displayName}__header`}>
            <h2 className={`${displayName}__title`}>
              Happens to the best of <br />
              <span className={`${displayName}__title--italic`}>
                us, let's reset it
              </span>
            </h2>
          </div>

          <div className={`${displayName}__form-text`}>
            Enter your email address and we'll send you a reset link
          </div>

          <form
            className={`${displayName}__form`}
            onSubmit={handleSubmit}
            noValidate
          >
            <div
              className={`${displayName}__field ${
                error
                  ? `${displayName}__field--shake ${displayName}__field--error`
                  : ""
              }`}
            >
              <div className={`${displayName}__input-area`}>
                <img
                  src={mailLogo}
                  alt="Email"
                  className={`${displayName}__input-icon`}
                />
                <input
                  id="email"
                  type="email"
                  className={`${displayName}__input`}
                  placeholder="Enter your email address"
                  value={email}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setEmail(e.target.value)
                  }
                  disabled={disableBtn}
                  autoComplete="email"
                />
              </div>
              {error && (
                <span className={`${displayName}__error-txt`} role="alert">
                  {error}
                </span>
              )}
            </div>

            <div className={`${displayName}__submit-btn`}>
              <button
                className={`${displayName}__submit ${
                  disableBtn ? `${displayName}__submit--loading` : ""
                }`}
                type="submit"
                disabled={disableBtn}
              >
                {disableBtn ? "Sending..." : "Send Reset Link"}
              </button>
            </div>
          </form>

          <div className={`${displayName}__back-section`}>
            <button
              type="button"
              className={`${displayName}__back-link`}
              onClick={handleBackToLogin}
            >
              ← Back to Login
            </button>
          </div>
        </>
      )}
    </FlyoutLayout>
  );
};

export default ForgotPassword;
